import { useClerk } from '@clerk/clerk-react';
import { CheckIcon, CrossIcon } from '@vrplatform/icons';
import {
  showErrorNotification,
  updateNotification,
  showNotification,
} from '@vrplatform/theme';
import { showWarnNotification } from '@vrplatform/theme';
import { hasValue } from '@vrplatform/utils';
import { useNetwork } from '@mantine/hooks';
import {
  type InfiniteData,
  type QueryObserverResult,
  type RefetchOptions,
  type RefetchQueryFilters,
  type QueryKey as TanstackQueryKey,
  useInfiniteQuery as useIQ,
  useMutation as useM,
  useQuery as useQ,
  useQueryClient,
} from '@tanstack/react-query';
import type { GeneratedSchema, Query } from '@vrplatform/graphql';
import * as gqlV2 from '@vrplatform/graphql';
import type { GQtyError } from '@vrplatform/graphql';
import type React from 'react';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import { GRAPHQL_URI, PLATFORM } from '../env';
import {
  captureSentryError,
  useTeam,
  type PollUntilCompletedProps,
  usePollUntilCompleted,
} from '../hooks';
import { useExtension } from '../hooks/useExtension';
import { isStorybook } from '../env';
import { getTeamScopedPlaceholderData } from '../utils';
import type { QueryGroup } from '../api/api';

export type { GQtyError, GeneratedSchema, Query, gqlV2 };

const iconColor = '#fff';
const iconSize = 18;

export type Client = ReturnType<typeof gqlV2.useHasuraClient>;

const context = createContext<Client>({
  client: undefined,
  subscribe: undefined,
  wrapped: undefined,
} as any);

// const clientFetcher = (input: Request<unknown, CfProperties<unknown>>) => {
//   // let url = params.url;

//   // Stopped working with finalytic/graphql@2.0.85 since body is not a string anymore
//   // if (params.body && typeof params.body === 'string') {
//   //   const operation = (params.body as string).includes('"query":"mutation')
//   //     ? 'mutation'
//   //     : 'query';

//   //   // Remove duplicates and get all query keys
//   //   const queryKeys = [
//   //     ...new Set(
//   //       [...(params.body as string).matchAll(/{[a-zA-Z]+_/g)].map((i) =>
//   //         i[0].slice(1, -1)
//   //       )
//   //     ),
//   //   ]
//   //     .filter((i) => !['count'].includes(i))
//   //     .map((key) => `&queryKey=${key}`)
//   //     .join('');

//   //   url = `${url}?operation=${operation}${queryKeys}`;
//   // }

//   return fetch(input);
// };

export function ExtensionGtqyProvider({
  children,
  token,
}: {
  children: React.ReactNode;
  token: string;
}) {
  return (
    <DirectTokenGtqyProvider accessToken={token}>
      {children}
    </DirectTokenGtqyProvider>
  );
}

function DirectTokenGtqyProvider({
  accessToken,
  children,
}: {
  accessToken: string;
  children: React.ReactNode;
}) {
  const ref = useRef<Client>();
  if (!ref.current) {
    function getTokenHeaders() {
      const headers: { [s: string]: string } = {
        Authorization: `Bearer ${accessToken}`,
        'X-Transaction-ID': Math.random().toString(36).substring(2, 9),
        'X-Verification-Key': '5db60e10-fe6f-4726-9128-d2708285892e',
      };
      if (PLATFORM) headers['Finalytic-Platform'] = PLATFORM;
      return headers;
    }

    // biome-ignore lint/correctness/useHookAtTopLevel: its not a hook
    ref.current = gqlV2.useHasuraClient({
      headers: () => getTokenHeaders(),
      subscriptions: true,
      attempts: 1,
      credentials: 'include',
      uri: GRAPHQL_URI,
    });
  }

  return createElement(context.Provider, { value: ref.current }, children);
}
export function GtqyClientProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useClerk();
  const { sendMessage } = useExtension();

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need to add getToken to dependencies
  const client = useMemo(() => {
    async function getToken() {
      const accessToken = await auth.session?.getToken({
        template: 'Hasura',
      });
      sendMessage({ message: 'token', data: { token: accessToken } });
      if (accessToken) {
        localStorage.setItem('at', accessToken);
      } else {
        localStorage.removeItem('at');
      }

      const headers: { [s: string]: string } = {
        Authorization: `Bearer ${accessToken}`,
        'X-Transaction-ID': Math.random().toString(36).substring(2, 9),
        'X-Verification-Key': '5db60e10-fe6f-4726-9128-d2708285892e',
      };
      if (PLATFORM) headers['Finalytic-Platform'] = PLATFORM;
      return headers;
    }

    // biome-ignore lint/correctness/useHookAtTopLevel: not a react hook
    return gqlV2.useHasuraClient({
      headers: async () => await getToken(),
      subscriptions: true,
      attempts: 1,
      credentials: 'include',
      uri: GRAPHQL_URI,
    });
  }, [auth.session?.id]); // Recreate when session changes

  return createElement(context.Provider, { value: client }, children);
}

export type V2MutationOptions<TData, _TArgs> = {
  onError?: (error: any) => void;
  onCompleted?: (data: TData) => void;
  invalidateQueryKeys?: (QueryKeyUnion | (string & {}))[];
  invalidateExact?: boolean;
  storybookQueryKey?: string;
};

function useMutationBasic<TData, TArgs>(
  fn: (mutation: gqlV2.Mutation, vars: TArgs) => TData,
  options?: V2MutationOptions<TData, TArgs>
) {
  const { client } = useContext(context);

  const queryClient = useQueryClient();

  const {
    mutateAsync,
    isPending: isLoading,
    error,
    data,
  } = useM({
    mutationFn: async (mutationOptions: { args: TArgs }) => {
      if (isStorybook && options?.storybookQueryKey) {
        return fetch(
          `${window.location.origin}/storybook/${options.storybookQueryKey}`,
          {
            method: 'POST',
          }
        ).then((res) => res.json() as Promise<TData>);
      }

      return await client.resolved<TData>(
        () => fn(client.mutation, mutationOptions.args),
        {
          noCache: true,
        }
      );
    },
    onSuccess: options?.onCompleted,
    onError: options?.onError,
    onSettled: () => {
      if (options?.invalidateQueryKeys) {
        options.invalidateQueryKeys.forEach((key) => {
          queryClient.invalidateQueries({
            queryKey: [key],
            exact: options?.invalidateExact,
          });
        });
        queryClient.invalidateQueries({ queryKey: ['default'] });
      }
    },
  });
  return [mutateAsync, { isLoading, error, data }] as const;
}

type HasuraInvalidateKey = QueryKeyUnion | (string & {});

const mergeInvalidateKeys = <T extends string>(
  initialKeys: readonly T[] | undefined,
  keys: readonly T[] | T | undefined
) => {
  const merged: T[] = [];

  if (Array.isArray(initialKeys)) merged.push(...initialKeys);
  if (Array.isArray(keys)) merged.push(...keys);
  if (typeof keys === 'string') merged.push(keys);

  return merged;
};

export function useInvalidateHasuraQueries(
  invalidateQueryKeys?: readonly HasuraInvalidateKey[]
) {
  const queryClient = useQueryClient();

  return useCallback(
    (keys?: readonly HasuraInvalidateKey[] | HasuraInvalidateKey) => {
      const toInvalidate = mergeInvalidateKeys(invalidateQueryKeys, keys);

      if (!toInvalidate.length) return;

      toInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key], type: 'all' });
      });
      queryClient.invalidateQueries({ queryKey: ['default'] });
    },
    [invalidateQueryKeys, queryClient]
  );
}

export function useInvalidateApiQueries(
  invalidateQueryGroups?: readonly QueryGroup[]
) {
  const queryClient = useQueryClient();

  return useCallback(
    (groups?: readonly QueryGroup[] | QueryGroup) => {
      const toInvalidate = mergeInvalidateKeys(invalidateQueryGroups, groups);

      if (!toInvalidate.length) return;

      toInvalidate.forEach((queryGroup) => {
        queryClient.invalidateQueries({
          queryKey: [queryGroup],
          type: 'all',
        });
      });
    },
    [invalidateQueryGroups, queryClient]
  );
}

export const useMutation = <TData, TArgs>(
  fn: (mutation: gqlV2.Mutation, vars: TArgs) => TData,
  options?: V2MutationOptions<TData, TArgs> & {
    successMessage?: {
      id?: string;
      message?: string;
      title?: string;
    };
    errorMessage?: {
      title?: string | ((err: Error) => string | undefined);
      message?: string | ((err: Error) => string | undefined);
    };
    hideNotification?: boolean;
  }
) => {
  const notifyRef = useId();

  const notifyId = options?.successMessage?.id || notifyRef;

  const [mutate, { isLoading, error }] = useMutationBasic(fn, {
    ...options,
    invalidateExact: options?.invalidateExact,
    invalidateQueryKeys: options?.invalidateQueryKeys,
    onCompleted: (data) => {
      if (options?.onCompleted) options.onCompleted(data);

      if (options?.hideNotification) return;

      if (options?.successMessage) {
        updateNotification({
          id: notifyId,
          message:
            options?.successMessage?.message ||
            'Sucessfully updated your action.',
          title: options?.successMessage?.title || 'Success!',
          color: 'teal',
          icon: <CheckIcon color={iconColor} size={iconSize} />,
          radius: 10,
          loading: false,
          autoClose: 3000,
        });
      }
    },
    onError: (error) => {
      const getTitle = (
        type: 'title' | 'message',
        defaultValue: string | undefined
      ) => {
        if (typeof options?.errorMessage?.[type] === 'function')
          return (options.errorMessage?.[type] as any)?.(error) || defaultValue;
        return options?.errorMessage?.[type] || defaultValue;
      };

      const title = getTitle('title', error.name);
      const message = getTitle('message', error.message);

      if (options?.hideNotification) return;

      if (options?.successMessage) {
        updateNotification({
          id: notifyId,
          title,
          message,
          color: 'red',
          icon: <CrossIcon color={iconColor} size={iconSize} />,
          radius: 10,
          loading: false,
          autoClose: 3000,
        });
      } else {
        showErrorNotification({
          title,
          message,
        });
      }
    },
  });

  const m: typeof mutate = async (...opts) => {
    if (options?.successMessage && !options?.hideNotification) {
      showNotification({
        id: notifyId,
        loading: true,
        title: 'Loading...',
        message: 'We will update you shortly.',
        autoClose: false,
        radius: 10,
      });
    }

    const result = await mutate(...opts);
    return result;
  };

  return { mutate: m, loading: isLoading, error };
};

export { useQueryClient };

export const useSubscription = <TData, TVariables = undefined>(
  fn: (sub: gqlV2.Subscription, variables: TVariables) => TData,
  variables?: TVariables,
  options?: {
    skip?: boolean;
    queryKey?: QueryKeyUnion | QueryKeyUnion[];
    keepPreviousData?: boolean;
    onSubscriptionData?: (data: TData) => void;
  }
): {
  data: TData | undefined;
  error?: any;
  isLoading: boolean;
  isFetching: boolean;
  isInitialLoading: boolean;
  isCalled: boolean;
  refetch: (
    options?: (RefetchOptions & RefetchQueryFilters) | undefined
  ) => Promise<QueryObserverResult<TData, Error>>;
} => {
  const errorRef = useRef<any>(undefined);
  const { client } = useContext(context);
  const uniqueKey = useRef('');
  const [team, , isFetchingTeam] = useTeam();
  const teamId = team?.id;
  const networkStatus = useNetwork();

  if (!uniqueKey.current) uniqueKey.current = fn.toString();

  const baseQueryKey: any[] = [
    options?.queryKey,
    uniqueKey.current,
    variables,
  ].filter(hasValue);
  const queryKey = [...baseQueryKey, teamId];

  const queryClient = useQueryClient();

  const { subscribe } = useContext(context);

  const skip = (!!options?.skip || isFetchingTeam) && !!networkStatus.online;

  useEffect(() => {
    const unsubscribe = subscribe(
      (s) => fn(s, variables!),
      (data) => {
        errorRef.current = undefined;

        // Tried fixing blib, but not luck
        // const skip =
        //   Array.isArray(data) &&
        //   data.length &&
        //   'id' in data[0] &&
        //   data.every((x) => !x.id);
        // if (skip) return;

        options?.onSubscriptionData?.(data);
        queryClient.setQueryData(queryKey, () => data);
      },
      (error) => {
        errorRef.current = error;
        // queryClient.setQueryData(queryKey, undefined);
      }
    );
    return () => {
      unsubscribe();
    };
  }, [
    options?.onSubscriptionData,
    fn,
    queryClient.setQueryData,
    queryKey,
    subscribe,
    variables,
  ]);

  const { isLoading, data, error, refetch, isFetching, isInitialLoading } =
    useQ<TData, Error>({
      queryKey,
      queryFn: () =>
        client.resolved(() => fn(client.query as any, variables!), {}),
      enabled: !skip,
      placeholderData: options?.keepPreviousData
        ? getTeamScopedPlaceholderData(teamId)
        : undefined,
    });

  return {
    isLoading,
    data: data as any,
    error: errorRef.current || error,
    isCalled: true,
    refetch,
    isFetching,
    isInitialLoading,
  };
};

export type QueryKeyUnion =
  | 'flows'
  | 'emailTemplates'
  | 'setupGuide'
  | 'taxStatements'
  | 'ownerStatements'
  | 'ownerStatementTemplates'
  | 'ownerStatementTemplateCollections'
  | 'settings'
  | 'automations'
  | 'automationTemplates'
  | 'bookingChannels'
  | 'apps'
  | 'connections'
  | 'tenantUsers'
  | 'teams'
  | 'users'
  | 'sources'
  | 'paymentLineClassifications'
  | 'paymentLines'
  | 'listings'
  | 'listingCollections'
  | 'listingOwners'
  | 'listingConnections'
  | 'reservations'
  | 'payments'
  | 'tasks'
  | 'auditLogs'
  | 'files'
  | 'scheduledEvents'
  | 'owners'
  | 'features'
  | 'featureApprovals'
  | 'featureEnabledTeams'
  | 'jobPlans'
  | 'actions'
  | 'token'
  | 'views'
  | 'issues'
  | 'activity'
  | 'accounts'
  | 'expenses'
  | 'deposits'
  | 'journalEntries'
  | 'customFees'
  | 'issueMessageOverwrites'
  | 'taxRates'
  | 'bankRecords'
  | 'recurringExpenses';

class HasuraQueryError extends Error {
  constructor(readonly message: string) {
    super(`Query error message: ${message}`);
    this.name = 'ReactQueryError';
  }
}

export type QueryKey =
  | (QueryKeyUnion | (string & {})) // allows autocomplete for union but also pass any string
  | (QueryKeyUnion | (string & {}))[];

function handleQueryError(
  error: Error,
  signal: AbortSignal,
  extra?: Record<string, string | boolean | undefined>
) {
  if (window?.location?.host === 'localhost:4009') return;

  const title = 'Query Error';
  const message = error?.message;

  const lower = message.toLowerCase();

  // hide error on speciific issues
  if (
    signal?.aborted ||
    !message ||
    message === `Unexpected token '<', "<!DOCTYPE "... is not valid JSON` ||
    message ===
      'Could not verify JWT: JWSError (CompactDecodeError Invalid number of parts: Expected 3 parts; got 1)'
  )
    return;

  const disableNotification =
    lower.includes('failed to fetch') ||
    lower.includes('load failed') ||
    lower.includes('jwtexpired') ||
    lower.includes('database query error') ||
    lower.includes('you are signed out') ||
    lower.includes(
      'unable to authenticate the request, you need to supply an active session'
    );

  const disableSentry =
    lower.includes('jwtexpired') ||
    lower.includes('signal is aborted without reason') ||
    lower.includes('you are signed out') ||
    lower.includes(
      'unable to authenticate the request, you need to supply an active session'
    );

  if (!disableSentry)
    captureSentryError(new HasuraQueryError(message), {
      level: 'error',
      fingerprint: ['{{ default }}', 'hasura-query', message],
      extra,
    });

  if (disableNotification) return;

  // TODO: this is only for now - will add this back in later
  if (lower.includes('not found in type')) {
    return;
    // title = 'Query Permission Error';
    // message = 'Missing user permissions on query.';
  }

  // ignore showing error message for weird undefined ID error when refocusing window after long time
  if (message.includes(`expecting a value for non-nullable variable: "id1"`))
    return;

  if (
    message.includes(
      'The operation exceeded the time limit of 60s allowed for this project'
    )
  ) {
    return showWarnNotification({
      title: 'Connection Timeout',
      message:
        'Due to a possible slow internet connection, or temporarily high load on our servers, the connection from your computer has timed out. Please try again later.',
    });
  }

  showErrorNotification({
    title,
    message,
  });
}

type BaseQueryOptions<TVariables> = {
  variables?: TVariables;
  skip?: boolean;
  queryKey?: QueryKey;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  refetchOnMount?: boolean;
  gcTime?: number;
  storybookQueryKey?: string;
  teamScoped?: boolean;
};

export const useQuery = <TData, TVariables = undefined>(
  fn: (query: GeneratedSchema['query'], variables: TVariables) => TData,
  options?: BaseQueryOptions<TVariables> & {
    keepPreviousData?: boolean;
    staleTime?: number;
    poll?: Omit<
      PollUntilCompletedProps,
      'refetchQuery' | 'skip' | 'isCompleted'
    > & {
      isCompleted: (data: TData | undefined) => boolean;
    };
  }
) => {
  const uniqueKey = useRef('');
  const networkStatus = useNetwork();
  const [team, , isFetchingTeam] = useTeam();
  const isTeamScoped = options?.teamScoped ?? true;
  const teamId = isTeamScoped ? team?.id : undefined;

  if (!uniqueKey.current) uniqueKey.current = fn.toString();

  const { client } = useContext(context);
  const variables = options?.variables || ({} as TVariables);
  const skip: boolean =
    (!!options?.skip || (isTeamScoped && isFetchingTeam)) &&
    !!networkStatus.online;

  const queryKeys = Array.isArray(options?.queryKey)
    ? options?.queryKey || []
    : options?.queryKey
      ? [options.queryKey]
      : [];

  const qk: TanstackQueryKey = [
    ...queryKeys,
    'global',
    uniqueKey.current,
    variables,
    networkStatus.online,
    ...(isTeamScoped ? [teamId] : []),
  ];

  const { isFetching, data, error, refetch, isInitialLoading } = useQ<
    TData,
    Error
  >({
    enabled: !skip,
    queryKey: qk,
    queryFn: async ({ signal }) => {
      if (isStorybook && options?.storybookQueryKey) {
        return await fetch(
          `${window.location.origin}/storybook/${options.storybookQueryKey}`,
          { signal }
        ).then((res) => res.json() as Promise<TData>);
      }

      return client
        .resolved(() => fn(client.query, variables), {
          fetchOptions: {
            signal,
          },
        })
        .catch((err: any) => {
          handleQueryError(err, signal, {
            query: uniqueKey.current,
            isOnline: networkStatus.online,
          });
          throw err;
        });
    },
    refetchOnWindowFocus: options?.refetchOnWindowFocus || false,
    refetchOnReconnect: options?.refetchOnReconnect,
    refetchOnMount: options?.refetchOnMount,
    placeholderData: options?.keepPreviousData
      ? getTeamScopedPlaceholderData(teamId)
      : undefined,
    staleTime:
      options?.staleTime ?? (options?.keepPreviousData ? 30_000 : undefined),
    gcTime: options?.gcTime,
    meta: {
      teamScoped: isTeamScoped,
    },
  });

  const isCompleted = useMemo(() => {
    return !!options?.poll?.isCompleted(data);
  }, [data, options?.poll?.isCompleted]);

  usePollUntilCompleted(
    options?.poll
      ? {
          isCompleted,
          skip,
          interval: options.poll.interval,
          onCompleted: options.poll.onCompleted,
          refetchQuery: refetch,
        }
      : undefined
  );

  return {
    data: data,
    error,
    refetch,
    isLoading: isFetching,
    isInitialLoading,
    isFetching,
    isCalled: true,
    queryKey: qk,
  };
};

type InfiniteQueryParams = {
  limit: number;
  offset: number;
};

export const useInfiniteQuery = <
  TRow,
  TData extends { aggregate: number; list: TRow[] },
  TVariables = undefined,
>(
  fn: (
    query: GeneratedSchema['query'],
    variables: TVariables,
    queryParams: InfiniteQueryParams
  ) => TData,
  options?: BaseQueryOptions<TVariables> & {
    pageLimit?: number;
    poll?: Omit<
      PollUntilCompletedProps,
      'refetchQuery' | 'skip' | 'isCompleted'
    > & {
      isCompleted: (data: InfiniteData<TData> | undefined) => boolean;
    };
  }
) => {
  const uniqueKey = useRef('');
  const networkStatus = useNetwork();
  const [team, , isFetchingTeam] = useTeam();
  const isTeamScoped = options?.teamScoped ?? true;
  const teamId = isTeamScoped ? team?.id : undefined;

  if (!uniqueKey.current) uniqueKey.current = fn.toString();

  const { client } = useContext(context);
  const variables = options?.variables || ({} as TVariables);
  const skip: boolean =
    (!!options?.skip || (isTeamScoped && isFetchingTeam)) &&
    !!networkStatus.online;

  const queryKeys = Array.isArray(options?.queryKey)
    ? options?.queryKey || []
    : options?.queryKey
      ? [options.queryKey]
      : [];

  const queryKey = [
    ...queryKeys,
    'global',
    uniqueKey.current,
    variables,
    ...(isTeamScoped ? [teamId] : []),
  ];

  const pageLimit = options?.pageLimit || 50;

  const {
    isFetching,
    data,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
  } = useIQ<TData, Error>({
    queryKey,
    queryFn: async (params) => {
      if (isStorybook && options?.storybookQueryKey) {
        return fetch(
          `${window.location.origin}/storybook/${options.storybookQueryKey}`,
          {
            signal: params.signal,
          }
        ).then((res) => res.json() as Promise<TData>);
      }

      return await client
        .resolved(
          () =>
            fn(client.query as any, variables, {
              limit: pageLimit,
              offset:
                (typeof params.pageParam === 'number' ? params.pageParam : 0) *
                pageLimit,
            }),
          {
            fetchOptions: {
              signal: params.signal,
            },
          }
        )
        .catch((err: any) => {
          handleQueryError(err, params.signal, {
            query: uniqueKey.current,
            isOnline: networkStatus.online,
          });
          throw err;
        });
    },
    enabled: !skip,
    getNextPageParam: (lastPage, pages) => {
      const hasNext = lastPage.aggregate > pages.length * pageLimit;
      return hasNext ? pages.length : null; // return null if no more pages
    },
    refetchOnWindowFocus: options?.refetchOnWindowFocus || false,
    placeholderData: isTeamScoped
      ? getTeamScopedPlaceholderData(teamId)
      : undefined,
    initialPageParam: 0,
    meta: {
      teamScoped: isTeamScoped,
    },
  });

  const isCompleted = useMemo(() => {
    return !!options?.poll?.isCompleted(data);
  }, [data, options?.poll?.isCompleted]);

  usePollUntilCompleted(
    options?.poll
      ? {
          isCompleted,
          skip,
          interval: options.poll.interval,
          onCompleted: options.poll.onCompleted,
          refetchQuery: refetch,
        }
      : undefined
  );

  return {
    data,
    error,
    refetch,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
  };
};

export function useGqtyClient() {
  const client = useContext(context);
  return client;
}
